import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, Settings2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FieldConfigRow {
  id: string;
  record_type: string;
  field_name: string;
  enabled: number;
  required: number;
  sort_order: number;
}

interface FieldState {
  fieldName: string;
  enabled: boolean;
  required: boolean;
  sortOrder: number;
}

const RECORD_TYPES = [
  { value: "client", label: "Client Intake" },
  { value: "case_note", label: "Case Notes" },
  { value: "activity", label: "Activities" },
  { value: "legal_case", label: "Legal Cases" },
  { value: "shelter", label: "Shelter" },
  { value: "departure", label: "Departure" },
  { value: "outcome", label: "Outcomes" },
  { value: "assault", label: "Sexual Assault" },
  { value: "trafficking", label: "Trafficking" },
];

// Default fields per record type (displayed if no config exists yet)
const DEFAULT_FIELDS: Record<string, string[]> = {
  client: [
    "first_name", "last_name", "date_of_birth", "gender", "race", "ethnicity",
    "primary_language", "phone", "email", "address", "city", "state", "zip",
    "ssn", "emergency_contact", "emergency_phone", "insurance", "income",
    "employment_status", "education_level", "veteran_status", "disability",
    "children_count", "referral_source",
  ],
  case_note: [
    "date", "type", "duration", "location", "staff", "narrative", "follow_up",
  ],
  activity: [
    "date", "type", "duration", "staff", "location", "description", "units",
  ],
  legal_case: [
    "case_number", "court", "judge", "case_type", "status", "filing_date",
    "next_hearing_date", "attorney", "notes",
  ],
  shelter: [
    "bed_assignment", "check_in_date", "check_out_date", "room", "floor",
  ],
  departure: [
    "departure_date", "departure_reason", "destination_type", "destination",
    "safety_plan", "follow_up_date",
  ],
  outcome: [
    "category", "measure", "pre_value", "post_value", "date",
  ],
  assault: [
    "incident_date", "location", "type", "relationship_to_offender",
    "reported_to_police", "forensic_exam",
  ],
  trafficking: [
    "type", "date_identified", "country_of_origin", "referral_source",
  ],
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FieldConfig() {
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState("client");
  const [fields, setFields] = useState<FieldState[]>([]);

  const { data, isLoading } = useQuery<{ data: FieldConfigRow[] }>({
    queryKey: ["field-config", selectedType],
    queryFn: () => api.get(`/admin/config/field-config/${selectedType}`),
  });

  // Populate fields from API or defaults
  useEffect(() => {
    if (data?.data && data.data.length > 0) {
      setFields(
        data.data.map((f) => ({
          fieldName: f.field_name,
          enabled: !!f.enabled,
          required: !!f.required,
          sortOrder: f.sort_order,
        })),
      );
    } else {
      const defaults = DEFAULT_FIELDS[selectedType] ?? [];
      setFields(
        defaults.map((name, idx) => ({
          fieldName: name,
          enabled: true,
          required: false,
          sortOrder: idx,
        })),
      );
    }
  }, [data, selectedType]);

  const saveMutation = useMutation({
    mutationFn: (payload: { fields: FieldState[] }) =>
      api.put(`/admin/config/field-config/${selectedType}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["field-config", selectedType] });
    },
  });

  function toggleEnabled(idx: number) {
    setFields((prev) =>
      prev.map((f, i) =>
        i === idx ? { ...f, enabled: !f.enabled, required: !f.enabled ? f.required : false } : f,
      ),
    );
  }

  function toggleRequired(idx: number) {
    setFields((prev) =>
      prev.map((f, i) => (i === idx ? { ...f, required: !f.required } : f)),
    );
  }

  function handleSave() {
    saveMutation.mutate({ fields });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Field Configuration</h2>
        <p className="text-sm text-muted-foreground">
          Enable, disable, or require fields for each record type
        </p>
      </div>

      {/* Record type selector */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Record Type</label>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="flex h-9 w-full max-w-xs rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {RECORD_TYPES.map((rt) => (
            <option key={rt.value} value={rt.value}>
              {rt.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading && <div className="h-64 animate-pulse rounded-lg border bg-muted" />}

      {!isLoading && fields.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Settings2 className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No fields configured for this record type.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && fields.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Field
                  </th>
                  <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">
                    Enabled
                  </th>
                  <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">
                    Required
                  </th>
                  <th className="px-4 py-2.5 text-center font-medium text-muted-foreground">
                    Order
                  </th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, idx) => (
                  <tr key={field.fieldName} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium">
                      {field.fieldName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={field.enabled}
                        onChange={() => toggleEnabled(idx)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={() => toggleRequired(idx)}
                        disabled={!field.enabled}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-center text-muted-foreground">
                      {field.sortOrder}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {saveMutation.isSuccess && (
        <div className="rounded-md bg-green-50 p-3 text-sm text-green-800">
          Field configuration saved.
        </div>
      )}

      {saveMutation.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {(saveMutation.error as Error)?.message ?? "Failed to save"}
        </div>
      )}

      {fields.length > 0 && (
        <Button className="gap-2" onClick={handleSave} loading={saveMutation.isPending}>
          <Save className="h-4 w-4" />
          Save Configuration
        </Button>
      )}
    </div>
  );
}
